import jsQR from "jsqr";

/**
 * Декодирует QR-код из выбранного пользователем файла-картинки.
 *
 * @throws Error если файл не картинка, слишком большой, или QR не удалось
 *         прочитать (например, размытый/повёрнутый).
 */
export async function decodeQrFromImage(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Выбранный файл не является изображением");
  }
  // 12 MB — здравый лимит; больше почти наверняка не имеет смысла и медленно.
  if (file.size > 12 * 1024 * 1024) {
    throw new Error("Файл слишком большой (максимум 12 МБ)");
  }

  const dataUrl = await readAsDataURL(file);
  const image = await loadImage(dataUrl);

  const canvas = document.createElement("canvas");
  // Ограничиваем размер для скорости; jsQR отлично справляется с ~1600px.
  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Не удалось создать canvas");
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const result = jsQR(imageData.data, canvas.width, canvas.height, {
    inversionAttempts: "attemptBoth",
  });

  if (!result || !result.data) {
    throw new Error("Не удалось найти QR-код на изображении");
  }
  return result.data.trim();
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Не удалось открыть изображение"));
    img.src = src;
  });
}
